import { makeAutoObservable } from "mobx";
import { debounce, DebounceCounter } from "src/common/debounce";
import { onError } from "src/common/onError";

import {
  RtCtrlColumnsSettings,
  RtCtrlColumnsView,
  RtCtrlData,
  RtCtrlFiltration,
  RtCtrlFixedColumns,
  RtCtrlHover,
  RtCtrlLoad,
  RtCtrlPagination,
  RtCtrlSelection,
  RtCtrlVerticalMode,
} from "./ctrl";
import { RtCtrlConfig } from "./ctrl/RtCtrlConfig";
import { RtCtrlResizable } from "./ctrl/RtCtrlResizable";
import { RtCtrlSort, singleSortClickHandler } from "./ctrl/RtCtrlSort";
import {
  isColumnConfigurable,
  RtColumn,
  RtColumnDef,
  RtColumnFixedSide,
} from "./RtColumn";
import { RtConfig, RtConfigAccess } from "./RtConfig";
import { RtRequest } from "./RtRequest";
import { RtResponse } from "./RtResponse";
import { SortClickHandler, SortOrderItem } from "./RtSort";
import { RtStoreParams } from "./RtStoreParams";
import {
  AbstractFilters,
  AbstractRow,
  RtKey,
  RtSelectionType,
  RtVerticalMode,
} from "./RtTypes";
import { applyColumnsOrder } from "./utils/applyColumnsOrder";
import { makeEmptyResponse } from "./utils/makeEmptyResponse";
import { makeNewRequest } from "./utils/makeNewRequest";

export class RtStore<TRow extends AbstractRow, TFilters extends AbstractFilters>
  implements
    RtCtrlColumnsView<TRow>,
    RtCtrlColumnsSettings,
    RtCtrlData<TRow>,
    RtCtrlFiltration<TFilters>,
    RtCtrlHover,
    RtCtrlLoad<TFilters>,
    RtCtrlSort,
    RtCtrlPagination,
    RtCtrlSelection,
    RtCtrlConfig<TFilters>,
    RtCtrlVerticalMode,
    RtCtrlResizable,
    RtCtrlFixedColumns
{
  constructor(protected readonly params: RtStoreParams<TRow, TFilters>) {
    this.prevRequest = makeNewRequest(params.defaultRequest);
    this.verticalMode = params.verticalMode ?? this.verticalMode;
    this.fixedLeftRaw = params.fixedLeftRaw ?? 0;
    this.fixedRightRaw = params.fixedRightRaw ?? 0;
    makeAutoObservable(this);
    if (params.columns) {
      this.setSrcColumns(params.columns);
    }
    this.setSelectionType(params.selectionType ?? null);
  }

  async init(columns?: RtColumn<TRow>[]) {
    try {
      if (!this.params.keepSelected) {
        this.setSelectedKeys([]);
      }
      if (columns) this.setSrcColumns(columns);
      this.lock();
      await this.loadConfig(); // раньше чем загрузится конфиг, незачем показывать данные.
      this.setResponse(makeEmptyResponse());
      await this.reload();
    } catch (e) {
      this.onError(e);
    } finally {
      this.unlock();
    }
  }

  get rows(): TRow[] {
    return this.response.rows;
  }

  response: RtResponse<TRow> = {
    rows: [],
    totalRows: 0,
  };

  setResponse(response: RtResponse<TRow>) {
    this.response = response;
  }

  prevRequest: RtRequest<TFilters> | null = null;

  setPrevRequest(r: RtRequest<TFilters> | null) {
    this.prevRequest = r;
  }

  get validPrevRequest(): RtRequest<TFilters> {
    const { prevRequest, params } = this;
    return prevRequest ?? makeNewRequest(params.defaultRequest);
  }

  prevError: Error | null = null;

  setPrevError(error: Error | null) {
    this.prevError = error;
  }

  loadNextRows(request: RtRequest<TFilters>): Promise<RtResponse<TRow>> {
    const { loader } = this.params;
    return typeof loader === "function"
      ? loader(request)
      : loader.load(request);
  }

  // --- loading indicator

  lockCounter = 0;

  lock() {
    this.lockCounter++;
  }

  unlock() {
    this.lockCounter--;
  }

  get loading(): boolean {
    return this.lockCounter !== 0;
  }

  async load(request: RtRequest<TFilters>): Promise<void> {
    try {
      this.lock();
      this.setPrevError(null);
      const result: RtResponse<TRow> = await this.loadNextRows(request);
      this.setResponse(result);
      this.setPrevRequest(request);
    } catch (error) {
      this.onError(error);
      this.setPrevError(error);
    } finally {
      this.unlock();
    }
  }

  reload(): Promise<void> {
    // const {prevRequest, params} = this;
    // console.log("reload prevRequest", JSON.parse(JSON.stringify(prevRequest)));
    // const request: RtRequest<TFilters> = prevRequest ?? makeNewRequest(params.defaultRequest);
    return this.load(this.validPrevRequest);
  }

  /**
   * Обновление данных в таблице, если с реальными данными происходят какие-то изменения.
   * Например, при удалении строк.
   */
  onDataChanged() {
    this.update({ page: 0 });
  }

  updateCounter: DebounceCounter = {};

  updateParams: Partial<RtRequest<TFilters>> = {};

  clearUpdateParams() {
    this.updateParams = {};
  }

  update(params: Partial<RtRequest<TFilters>>) {
    // Нельзя пытаться оптимизировать запросы путём сравнения с существующими параметрами.
    // Потому что update({page:0}) вызывается для перезагрузки данных после манипуляций с данными (например, после удаления)
    this.updateParams = {
      ...this.prevRequest,
      ...this.updateParams,
      ...params,
    };
    debounce(this.updateCounter, 100, () => {
      this.load(makeNewRequest(this.updateParams));
      this.clearUpdateParams();
    });
  }

  // columns
  srcColumns: RtColumn<TRow>[] = [];

  setSrcColumns(columns: RtColumn<TRow>[]) {
    this.srcColumns = columns;
  }

  get orderedColumns(): RtColumn<TRow>[] {
    const configurableKeys = new Set(
      this.srcColumns.filter(isColumnConfigurable).map((column) => column.key),
    );
    const orderedColumns = applyColumnsOrder(
      this.srcColumns,
      this.columnsOrder.filter((key) => configurableKeys.has(key)),
    );
    return [
      ...orderedColumns.filter(
        (column) => column.capabilities?.fixed === "left",
      ),
      ...orderedColumns.filter((column) => !column.capabilities?.fixed),
      ...orderedColumns.filter(
        (column) => column.capabilities?.fixed === "right",
      ),
    ];
  }

  get visibleColumns(): RtColumn<TRow>[] {
    const { hiddenColumnKeys } = this;
    return this.orderedColumns.filter(
      (column) =>
        !isColumnConfigurable(column) || !hiddenColumnKeys.has(column.key),
    );
  }

  hiddenColumnKeys = new Set<string>();

  protected setHiddenColumnsKeys(columnsKeys: string[]) {
    this.hiddenColumnKeys = new Set(columnsKeys);
  }

  changeColumnVisibility(columnKey: string, visible: boolean): void {
    const column = this.srcColumns.find(({ key }) => key === columnKey);
    if (!isColumnConfigurable(column)) return;
    if (visible) {
      this.hiddenColumnKeys.delete(columnKey);
    } else {
      this.hiddenColumnKeys.add(columnKey);
    }
    this.updateConfig({
      hiddenColumnsKeys: Array.from(this.hiddenColumnKeys),
    });
  }

  canColumnHide(columnKey: string): boolean {
    const column = this.srcColumns.find(({ key }) => key === columnKey);
    if (!isColumnConfigurable(column)) return false;
    // запрещено прятать последний столбец
    if (
      this.visibleColumns.length === 1 &&
      this.visibleColumns[0]?.key === columnKey
    )
      return false;
    return true;
  }

  columnsOrder: string[] = [];

  protected setColumnsOrder(newOrder: string[]) {
    this.columnsOrder = newOrder;
  }

  changeColumnsOrder(newOrder: string[]) {
    this.setColumnsOrder(newOrder);
    this.updateConfig({
      columnsOrder: newOrder,
    });
  }

  columnWidths: Record<string, number | string> = {};

  setColumnWidths(dict: Record<string, number | string>) {
    this.columnWidths = dict;
  }

  changeColumnWidth(
    columnKey: string,
    width: number | undefined,
    noSave?: boolean,
  ) {
    const column = this.srcColumns.find(({ key }) => key === columnKey);
    if (column?.capabilities?.resizable === false) return;
    if (!width) {
      delete this.columnWidths[columnKey];
    } else {
      this.columnWidths[columnKey] = width;
    }
    if (!noSave) {
      this.updateConfig({ columnWidths: this.columnWidths });
    }
  }

  getColumnWidth(column: RtColumnDef): string | undefined {
    const width: number | string | undefined =
      this.columnWidths[column.key] ?? column.width ?? column.minWidth;
    return typeof width === "number" ? `${width}px` : width;
  }

  // fixed columns
  get fixedColumns(): ReadonlyMap<string, RtColumnFixedSide> {
    const { fixedLeftRaw, fixedRightRaw, orderedColumns, visibleColumns } =
      this;
    const result = new Map<string, RtColumnFixedSide>();
    const visibleKeys = new Set(visibleColumns.map(({ key }) => key));

    orderedColumns.slice(0, fixedLeftRaw).forEach(({ key }) => {
      if (visibleKeys.has(key)) result.set(key, "left");
    });
    if (fixedRightRaw > 0) {
      orderedColumns.slice(-fixedRightRaw).forEach(({ key }) => {
        if (visibleKeys.has(key)) result.set(key, "right");
      });
    }
    visibleColumns.forEach(({ key, capabilities }) => {
      if (capabilities?.fixed) result.set(key, capabilities.fixed);
    });

    return result;
  }

  fixedLeftRaw: number = 0;

  setFixedLeftRaw(columnsCount: number) {
    this.fixedLeftRaw = columnsCount;
  }

  changeFixedLeftRaw(columnsCount: number) {
    this.setFixedLeftRaw(columnsCount);
    this.updateConfig({ fixedLeftRaw: columnsCount });
  }

  fixedRightRaw: number = 0;

  setFixedRightRaw(columnsCount: number) {
    this.fixedRightRaw = columnsCount;
  }

  changeFixedRightRaw(columnsCount: number) {
    this.setFixedRightRaw(columnsCount);
    this.updateConfig({ fixedRightRaw: columnsCount });
  }

  get rowKeyField() {
    return this.params.rowKey;
  }

  getRowKey(row: TRow): RtKey {
    const { rowKey } = this.params;
    return typeof rowKey === "function"
      ? rowKey(row)
      : getKeyFromRow(row, rowKey);
  }

  // sort
  get sortOrder(): SortOrderItem[] {
    return (this.prevRequest ?? this.params.defaultRequest)?.sortOrder ?? [];
  }

  protected setSortOrder(sortOrder: SortOrderItem[]) {
    this.prevRequest = {
      ...(this.prevRequest ?? makeNewRequest()),
      sortOrder,
    };
  }

  sort(sortOrder: SortOrderItem[]) {
    this.update({ sortOrder });
    this.updateConfig({ sortOrder });
  }

  onSortClick(columnKey: string | undefined, handler?: SortClickHandler) {
    const realHandler =
      handler ?? this.params.sortClickHandler ?? singleSortClickHandler;
    this.sort(realHandler(columnKey, this.sortOrder));
  }

  // selection
  selectionType: RtSelectionType | null = null;

  setSelectionType(type: RtSelectionType | null) {
    this.selectionType = type;
  }

  selectedSet = new Set<RtKey>();

  get selectedKeys(): RtKey[] {
    return Array.from(this.selectedSet);
  }

  setSelectedKeys(keys: RtKey[]): void {
    this.selectedSet = new Set(keys);
    this.updateRowsCache(keys);
  }

  isSelected(rowKey: RtKey): boolean {
    return this.selectedSet.has(rowKey);
  }

  changeSelection(rowKey: RtKey, checked: boolean | "toggle"): void {
    const { selectionType } = this;
    if (!selectionType) return;
    const needCheck: boolean =
      typeof checked === "boolean" ? checked : !this.isSelected(rowKey);
    if (needCheck) {
      if (selectionType === "radio") {
        this.selectedSet.clear();
      }
      this.selectedSet.add(rowKey);
      this.updateRowsCache([rowKey]);
    } else {
      this.selectedSet.delete(rowKey);
    }
  }

  // Пока временная затычка
  protected rowsCache: Record<RtKey, TRow> = {};

  protected updateRowsCache(rowKeys: RtKey[]) {
    rowKeys.forEach((key) => {
      const selectedRow = this.rows.find((row) => this.getRowKey(row) === key);
      if (selectedRow) {
        this.rowsCache[key] = selectedRow;
      }
    });
  }

  get selectedRows(): TRow[] {
    const result: TRow[] = [];
    this.selectedKeys.forEach((key) => {
      const selectedRow = this.rows.find((row) => this.getRowKey(row) === key);
      if (selectedRow) {
        // здесь можно было бы актуализировать кеш, но изменение стора внутри get может привести к бесконечному циклу обновлений
        // поэтому кеш наполняется из setSelectedKeys и changeSelection
        result.push(selectedRow);
      } else {
        const oldRow = this.rowsCache[key];
        if (oldRow) {
          result.push(oldRow);
        }
      }
    });
    return result;
  }

  get rowSelectionDisabled(): ((row: TRow) => boolean) | undefined {
    return this.params.rowSelectionDisabled;
  }

  // hover

  hoverRowIndex: number | null = null;

  setHoverRowIndex(index: number | null) {
    this.hoverRowIndex = index;
  }

  // vertical mode
  verticalMode: RtVerticalMode = "normal";

  setVerticalMode(mode: RtVerticalMode) {
    this.verticalMode = mode;
  }

  // pagination
  get page(): number {
    return this.prevRequest?.page ?? 0;
  }

  setPage(page: number): void {
    this.update({ page });
  }

  get pageSize(): number | undefined {
    return this.prevRequest?.pageSize;
  }

  setPageSize(pageSize: number | undefined): void {
    if (pageSize !== this.pageSize) {
      this.update({ pageSize, page: 0 });
      this.updateConfig({ pageSize });
    }
  }

  get totalRows(): number {
    return this.response.totalRows;
  }

  // filtration
  get filters(): TFilters | undefined {
    const { prevRequest, params } = this;
    return (prevRequest ?? params.defaultRequest)?.filters;
  }

  get defaultFilters(): TFilters | undefined {
    return this.params.defaultRequest?.filters;
  }

  changeFilters(filters: TFilters | undefined): void {
    this.update({ filters, page: 0 });
    this.updateConfig({ filters });
  }

  changeSingleFilter<Filter extends keyof TFilters>(
    filter: Filter,
    value: TFilters[Filter],
  ) {
    this.changeFilters({
      ...(this.filters as TFilters),
      [filter]: value,
    });
  }

  // config
  config: RtConfig<TFilters> | null = null;

  setConfig(newConfig: RtConfig<TFilters> | null) {
    this.config = newConfig;
  }

  get configBuzy(): boolean {
    return this.configLockCounter !== 0;
  }

  configLockCounter = 0;

  configLock() {
    this.configLockCounter++;
  }

  configUnlock() {
    this.configLockCounter--;
  }

  protected async onConfigAccess(
    task: (configAccess: RtConfigAccess<TFilters>) => Promise<void>,
  ) {
    const { configAccess } = this.params;
    if (configAccess) {
      try {
        this.configLock();
        await task(configAccess);
      } finally {
        this.configUnlock();
      }
    }
  }

  protected loadConfig() {
    return this.onConfigAccess(async (configAccess) => {
      const newConfig = await configAccess.load();
      this.setConfig(newConfig);
      if (newConfig) {
        const {
          pageSize,
          sortOrder,
          filters,
          columnsOrder,
          hiddenColumnsKeys,
          columnWidths,
        } = newConfig;
        const request: RtRequest<TFilters> = { ...this.validPrevRequest };
        if (pageSize) request.pageSize = pageSize;
        if (sortOrder) request.sortOrder = sortOrder;
        if (filters) request.filters = filters;
        this.setPrevRequest(request);
        this.setColumnsOrder(columnsOrder ?? []);
        this.setHiddenColumnsKeys(hiddenColumnsKeys ?? []);
        this.setColumnWidths(columnWidths ?? {});
      }
    });
  }

  protected saveConfig(newConfig: RtConfig<TFilters> | null) {
    return this.onConfigAccess(async (configAccess) => {
      await configAccess.save(newConfig);
      this.setConfig(newConfig);
    });
  }

  updateConfig(configPart: Partial<RtConfig<TFilters>> | null): void {
    const config = configPart
      ? {
          ...this.config,
          ...configPart,
        }
      : null;
    this.saveConfig(config).catch(this.onError);
  }

  get onError(): (error: Error) => void {
    return this.params.onError ?? onError;
  }
}

const getKeyFromRow = <TRow extends AbstractRow>(
  row: TRow,
  field: keyof TRow,
): RtKey => {
  const value = row[field];
  return typeof value === "number" ? value : String(value);
};
